import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  EventEmitter,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import {
  IServiceTemplateSummary,
  ServiceTemplateStatus,
} from '../../../../core/models/service-template/service-template.model';
import { IServiceCategory } from '../../../../core/models/service-category.model';

@Component({
  selector: 'irembogov-template-selection',
  templateUrl: './template-selection.component.html',
  styleUrls: ['./template-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class TemplateSelectionComponent
  implements OnInit, OnDestroy, OnChanges
{
  private destroy$ = new Subject<void>();

  @Input() currentTemplateId?: string; // The template currently being used in the change request

  @Output() templateSelected = new EventEmitter<IServiceTemplateSummary>();
  @Output() createWithoutTemplate = new EventEmitter<void>();

  templates: IServiceTemplateSummary[] = [];
  categories: IServiceCategory[] = [];
  categoryMap: { [key: string]: string } = {};
  isLoading = false;

  // Current template that might not be in the current page
  currentTemplate: IServiceTemplateSummary | null = null;
  isLoadingCurrentTemplate = false;

  // Filter options
  selectedCategory = '';
  searchTerm = '';

  // Pagination
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  totalPages = 0;

  // Preview state
  selectedTemplate: IServiceTemplateSummary | null = null;
  showPreviewModal = false;

  // Use template confirmation state
  templateToUse: IServiceTemplateSummary | null = null;
  showUseTemplateModal = false;

  constructor(
    private serviceTemplateService: ServiceTemplateService,
    private serviceChangeRequestService: ServiceChangeRequestService
  ) {}

  ngOnInit(): void {
    this.loadCategories();
    this.loadPublishedTemplates();
    this.checkAndLoadCurrentTemplate();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentTemplateId']) {
      this.checkAndLoadCurrentTemplate();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCategories(): void {
    this.serviceChangeRequestService
      .getAllServiceCategories()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.categories = response.data || [];
          this.categoryMap = this.categories.reduce((map, category) => {
            map[category.id] = category.name;
            return map;
          }, {} as { [key: string]: string });
        },
        error: () => {
          this.categories = [];
        },
      });
  }

  private checkAndLoadCurrentTemplate(): void {
    if (!this.currentTemplateId) {
      this.currentTemplate = null;
      return;
    }

    // First check if the current template is in the loaded templates
    const foundTemplate = this.templates.find(
      t => t.id === this.currentTemplateId
    );
    if (foundTemplate) {
      this.currentTemplate = foundTemplate;
      return;
    }

    // If not found and we haven't loaded it yet, load it via API
    if (
      !this.currentTemplate ||
      this.currentTemplate.id !== this.currentTemplateId
    ) {
      this.loadCurrentTemplateById();
    }
  }

  private loadCurrentTemplateById(): void {
    if (!this.currentTemplateId || this.isLoadingCurrentTemplate) {
      return;
    }

    this.isLoadingCurrentTemplate = true;

    this.serviceTemplateService
      .getTemplateById(this.currentTemplateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoadingCurrentTemplate = false))
      )
      .subscribe({
        next: response => {
          this.currentTemplate = response.data;
        },
        error: () => {
          // If we can't load the template, set it to null
          this.currentTemplate = null;
        },
      });
  }

  private loadPublishedTemplates(): void {
    this.isLoading = true;

    const queryParams = this.serviceTemplateService.buildQueryParams({
      page: this.currentPage,
      size: this.pageSize,
      status: ServiceTemplateStatus.PUBLISHED,
      categoryId: this.selectedCategory || undefined,
      search: this.searchTerm || undefined,
      sortBy: 'name',
      sortDirection: 'asc',
    });

    this.serviceTemplateService
      .getTemplates(queryParams)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: response => {
          this.templates = response.data.content || [];
          this.totalElements = response.data.totalElements || 0;
          this.totalPages = response.data.totalPages || 0;
          // Check if current template is in the newly loaded templates
          this.checkAndLoadCurrentTemplate();
        },
        error: () => {
          this.templates = [];
        },
      });
  }

  onCategoryChange(categoryId: string): void {
    this.selectedCategory = categoryId;
    this.currentPage = 0;
    this.loadPublishedTemplates();
  }

  onSearchChange(): void {
    this.currentPage = 0;
    this.loadPublishedTemplates();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadPublishedTemplates();
  }

  viewTemplate(template: IServiceTemplateSummary): void {
    this.selectedTemplate = template;
    this.showPreviewModal = true;
  }

  closePreview(): void {
    this.showPreviewModal = false;
    this.selectedTemplate = null;
  }

  useTemplate(template: IServiceTemplateSummary): void {
    this.templateToUse = template;
    this.showUseTemplateModal = true;
    // Close preview modal if it's open
    if (this.showPreviewModal) {
      this.closePreview();
    }
  }

  confirmUseTemplate(): void {
    if (this.templateToUse) {
      this.templateSelected.emit(this.templateToUse);
      this.cancelUseTemplate();
    }
  }

  cancelUseTemplate(): void {
    this.showUseTemplateModal = false;
    this.templateToUse = null;
  }

  onCreateWithoutTemplate(): void {
    this.createWithoutTemplate.emit();
  }

  isCurrentTemplate(templateId: string): boolean {
    return this.currentTemplateId === templateId;
  }

  get hasCurrentTemplate(): boolean {
    return !!this.currentTemplateId;
  }

  get currentTemplateName(): string | null {
    if (!this.currentTemplateId) {
      return null;
    }

    // First check if it's in the loaded templates
    const foundTemplate = this.templates.find(
      t => t.id === this.currentTemplateId
    );
    if (foundTemplate) {
      return foundTemplate.name;
    }

    // Then check if we have it loaded separately
    if (this.currentTemplate) {
      return this.currentTemplate.name;
    }

    // If we're still loading, show loading state
    if (this.isLoadingCurrentTemplate) {
      return 'Loading...';
    }

    // Fallback
    return 'Unknown Template';
  }

  get createButtonText(): string {
    if (this.hasCurrentTemplate) {
      const templateName = this.currentTemplateName;
      return templateName
        ? `Continue Using (${templateName})`
        : 'Continue Using Template';
    }
    return 'Create Without Template';
  }

  get filteredCategories() {
    return this.categories.filter(category =>
      this.templates.some(template => template.categoryId === category.id)
    );
  }
}
